/* eslint-disable @typescript-eslint/default-param-last */

import type { Reference, StoreObject } from '@apollo/client';
import { InMemoryCache } from '@apollo/client';

type StoreObjectOrReference = StoreObject | Reference;

export const createCache = () =>
  new InMemoryCache({
    typePolicies: {
      AiGeneratedImage: {
        fields: {
          edits: {
            merge: true,
          },
          // Client-only field that's used to mark when we're polling this image
          // for its generation state to prevent multiple simultaneous polling
          // queries. It seems that if we don't default this value, the cache
          // doesn't update properly for queries that return generated images.
          pollingConsumerId: {
            read: (id: string | null = null) => id,
          },
          clientStatus: {
            // In certain cases, a user could have a request timeout that completes async after the fact.
            // If they request the image again, and the generation has completed, don't return the
            // client status as timed out as we now have a result.
            read: (clientStatus: string | null = null, { readField }) =>
              readField('status') ? null : clientStatus,
          },
        },
      },
      AiOriginalImage: {
        fields: {
          aiGeneratedImages: {
            merge(
              existing: StoreObjectOrReference[] = [],
              incoming: StoreObjectOrReference[] = [],
              { readField },
            ) {
              const existingIds = existing.map(e => readField<number>('id', e));

              const uniqueIncoming = incoming.filter(
                i => !existingIds.includes(readField<number>('id', i)),
              );

              const merged = [...existing, ...uniqueIncoming];

              return merged;
            },
          },
        },
      },
      Profile: {
        keyFields: ['user', ['id']],
      },
      Query: {
        fields: {
          aiOriginalImageAnonymous(_, { args, toReference }) {
            return toReference({
              __typename: 'AiOriginalImage',
              id: Number(args!.id),
            });
          },
          aiOriginalImage(image, { args, toReference }) {
            const reference = toReference({
              __typename: 'AiOriginalImage',
              id: Number(args!.id),
            });

            // Cache `null` responses to prevent additional requests for 404s
            // Whenever we do this, we'll want to make sure to use the
            // `useInvalidateEmptyQuery` hook to invalidate the cache when we
            // want the next query to make another request to the server.
            if (image === null && args?.id) return null;

            return reference;
          },
          aiGeneratedImage(_, { args, toReference }) {
            return toReference({
              __typename: 'AiGeneratedImage',
              id: Number(args!.id),
            });
          },
          aiDisplayImage(_, { args, toReference }) {
            return toReference({
              __typename: 'AiDisplayImage',
              id: Number(args!.id),
            });
          },
        },
      },
    },
  });
